import { validateForm, showError } from 'application/utils/form-validation'

export function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (character) =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      })[character]!
  )
}

/**
 * Класс для управления формой проверки сертификата.
 * Обрабатывает ввод номера сертификата, отправку формы и отображение результатов проверки.
 */
export class CertificateCheckForm {
  private container: HTMLElement
  private form: HTMLFormElement
  private submitBtn: HTMLButtonElement
  private supportEmail: string
  private messageElement: HTMLElement | null = null

  /**
   * Создает экземпляр формы проверки сертификата.
   * @param {HTMLElement} container - HTML-элемент контейнера, содержащий форму.
   */
  constructor(container: HTMLElement) {
    this.container = container
    this.supportEmail =
      container.dataset.supportEmail || 'helpme@claustrophobia.com'

    this.init()
  }

  /**
   * Инициализирует форму: находит необходимые элементы, устанавливает обработчики событий
   * и настраивает валидацию ввода.
   */
  private init() {
    this.form = this.container.querySelector<HTMLFormElement>('form')!
    this.submitBtn = this.form.querySelector<HTMLButtonElement>(
      'button[type="submit"]'
    )!

    this.form.addEventListener('submit', this.onSubmit)

    const input = this.form.querySelector<HTMLInputElement>('input')
    this.submitBtn.disabled = true

    if (input) {
      input.addEventListener('input', () => {
        this.submitBtn.disabled = input.value.trim() === ''
      })
    }
  }

  /**
   * Обработчик события отправки формы.
   * Предотвращает стандартное поведение формы, удаляет предыдущие сообщения,
   * выполняет валидацию и отправляет данные на сервер.
   * @param {Event} event - Событие отправки формы.
   */
  private onSubmit = (event: Event) => {
    event.preventDefault()

    if (this.messageElement) {
      this.messageElement.remove()
      this.messageElement = null
    }

    if (!validateForm(this.form)) {
      return
    }

    this.fetchForm()
  }

  /**
   * Отправляет данные формы на сервер через GET-запрос.
   * Показывает индикатор загрузки во время выполнения запроса и обрабатывает результат.
   * @return {Promise<boolean>} Promise, который разрешается в true при успешной отправке, false при ошибке.
   */
  private async fetchForm(): Promise<boolean> {
    try {
      const formData = new FormData(this.form)
      const action = this.form.getAttribute('action')

      // Формируем URL с параметрами для GET запроса
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const params = new URLSearchParams(formData as any)
      const url = `${action}?${params.toString()}`

      this.submitBtn.classList.add('loading')

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      })

      const data = await response.json()
      this.renderResult(data.status, data.text)
    } catch (error) {
      this.renderError(
        'Ошибка при отправке формы. Пожалуйста, попробуйте позже.'
      )
      console.error('Error submitting form:', error)
      return false
    } finally {
      this.submitBtn.classList.remove('loading')
    }

    return true
  }

  /**
   * Обрабатывает результат проверки сертификата и вызывает соответствующий метод отображения.
   * @param {string} status - Статус сертификата ('expired', 'used', 'refunded', 'success', 'notfound').
   * @param {string} text - Текст сообщения для отображения пользователю.
   */
  private renderResult(status: string, text: string) {
    switch (status) {
      case 'expired':
        this.renderExpired(text)
        break
      case 'used':
      // Возврат покупки: сертификат больше не действует. Показываем тем же
      // экраном, что и погашенный, иначе статус уходил в default и клиент
      // не получал никакого ответа (ревью Codex Astra).
      case 'refunded':
        this.renderUsed(text)
        break
      case 'success':
        this.renderSuccess(text)
        break
      case 'notfound':
        this.renderNotFound()
        break
      default:
        // eslint-disable-next-line no-console
        console.debug('Unknown certificate status:', status)
    }
  }

  /**
   * Отображает сообщение о том, что срок действия сертификата истёк.
   * @param {string} text - Текст сообщения для отображения.
   */
  private renderExpired(text: string) {
    this.renderMessage({
      icon: 'cert-expired',
      title: 'Срок действия сертификата истёк',
      message: text
    })
  }

  /**
   * Отображает сообщение о том, что сертификат уже был использован.
   * @param {string} text - Текст сообщения для отображения.
   */
  private renderUsed(text: string) {
    this.renderMessage({
      icon: 'cert-used',
      title: 'Сертификат уже использован',
      message: text
    })
  }

  /**
   * Отображает сообщение об успешной проверке сертификата.
   * @param {string} text - Текст сообщения для отображения.
   */
  private renderSuccess(text: string) {
    this.renderMessage({
      icon: 'cert-success',
      title: 'Сертификат действителен',
      message: text
    })
  }

  /**
   * Отображает сообщение о том, что сертификат с указанным номером не найден.
   */
  private renderNotFound() {
    const supportEmail = escapeHtml(this.supportEmail)

    this.renderMessage({
      icon: 'cert-not-found',
      title: 'Сертификат не&nbsp;найден',
      message: `Проверьте номер или код сертификата либо напишите нам&nbsp;на <a href="mailto:${supportEmail}">${supportEmail}</a>`
    })
  }

  /**
   * Отображает сообщение об ошибке при проверке сертификата.
   * @param {string} message - Текст сообщения об ошибке.
   */
  private renderError(message: string) {
    console.error('Error:', message)
    showError(this.form, message)
  }

  /**
   * Отображает сообщение с иконкой, заголовком и текстом.
   * Удаляет предыдущее сообщение, если оно существует.
   * @param {Object} params - Параметры сообщения.
   * @param {string} params.icon - ID иконки для отображения в SVG спрайте.
   * @param {string} [params.title] - Заголовок сообщения (опционально).
   * @param {string} [params.message] - Текст сообщения (опционально).
   */
  private renderMessage({
    icon,
    title,
    message
  }: {
    icon: string
    title?: string
    message?: string
  }) {
    if (this.messageElement) {
      this.messageElement.remove()
    }

    this.messageElement = this.createMessageElement({
      icon,
      title: title || '',
      message: message || ''
    })

    this.container.appendChild(this.messageElement)
  }

  /**
   * Создает HTML-элемент сообщения с указанными параметрами.
   * @param {Object} params - Параметры для создания элемента.
   * @param {string} params.icon - ID иконки для отображения в SVG спрайте.
   * @param {string} params.title - Заголовок сообщения.
   * @param {string} params.message - Текст сообщения.
   * @return {HTMLElement} Созданный HTML-элемент с сообщением.
   */
  private createMessageElement({
    icon,
    title,
    message
  }: {
    icon: string
    title: string
    message: string
  }): HTMLElement {
    const container = document.createElement('div')
    container.classList.add('certificate-check-form__status')

    const titleTemplate = title
      ? `<b class="certificate-check-form__status-title">${title}</b>`
      : ''
    const messageTemplate = message
      ? `<p class="certificate-check-form__status-text">${message}</p>`
      : ''

    container.innerHTML = `
      <span class="certificate-check-form__status-icon">
        <svg>
          <use href="#${icon}"></use>
        </svg>
      </span>
      ${titleTemplate}
      ${messageTemplate}
    `
    return container
  }
}
